<template>
    <div ref="container" class="animated-html-text"></div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { HIGHLIGHT_TEXT_COLOR_LIST } from '../../const/trendReportGraphData';

const props = defineProps<{
    html: string;
    isActive: boolean;
    duration?: number;
    showAllImmediately?: boolean;
    moduleName?: string;
    textType?: string;
}>();

const emit = defineEmits(['done', 'sentenceDone']);

const container = ref<HTMLElement | null>(null);

function getColoredHtml(body: string, moduleName?: string, skipBackgroundColor = false): string {
    let bodyText = body;

    if (props.textType !== 'body') {
        return bodyText;
    }

    const moduleColorList = HIGHLIGHT_TEXT_COLOR_LIST.find(item => item.ModuleName === moduleName)?.ColorList ?? [];

    // @name@ 패턴 처리: 구분자 제거, 태그 없이 일반 텍스트로 표시 (키워드 매칭에서 제외)
    bodyText = bodyText.replace(/@([^@]+)@/g, '<span class="kw-no-highlight">$1</span>');

    // #name# 패턴 처리: 구분자 제거, 컬러 태그 스타일 적용
    bodyText = bodyText.replace(/#([^#]+)#/g, (_match, name) => {
        const colorItem = moduleColorList.find(item => item.name === name);
        if (colorItem) {
            const bgColor = (colorItem as { name: string; color: string; backgroundColor?: string }).backgroundColor;
            const backgroundColorStyle =
                !skipBackgroundColor && bgColor
                    ? `background-color: ${bgColor}; padding: 0 0.8rem; border-radius: 5rem;`
                    : '';
            const dataAttributes = bgColor
                ? ` data-has-bg="true" data-bg-color="${bgColor}" data-bg-padding="0 0.8rem" data-bg-radius="5rem"`
                : '';
            return `<span class="kw-highlighted" style="color: ${colorItem.color};${backgroundColorStyle}"${dataAttributes}>${name}</span>`;
        }
        return name;
    });

    if (moduleColorList.length === 0) {
        return bodyText;
    }

    /**
     * 텍스트 치환 로직:
     * 1. DOM을 사용하여 텍스트 노드만 치환 (이미 HTML 태그로 감싸진 부분은 자동으로 제외됨)
     * 2. 긴 단어부터 먼저 치환하도록 정렬 (예: "체지방량"이 "체지방"보다 먼저 치환되어야 함)
     * 3. 모든 키워드의 매칭을 먼저 수집한 후, 겹치는 매칭 중 긴 것을 우선 선택
     * 4. 역순으로 치환하여 offset 변경 문제 방지
     */
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = bodyText;

    // 긴 단어부터 먼저 치환하도록 정렬 (예: "체지방량"이 "체지방"보다 먼저 치환되어야 함)
    const sortedColorList = [...moduleColorList].sort((a, b) => b.name.length - a.name.length);

    function replaceInTextNode(node: Node) {
        if (node.nodeType === Node.TEXT_NODE) {
            let text = node.textContent || '';
            const originalText = text;

            // 1단계: 모든 키워드의 매칭을 찾아 수집
            // 각 키워드("체지방량", "체지방", "지방" 등)에 대해 텍스트에서 모든 매칭 위치를 찾음
            const allMatches: Array<{
                index: number;
                length: number;
                color: string;
                backgroundColor?: string;
                text: string;
            }> = [];

            sortedColorList.forEach(item => {
                // 정규식 특수문자 이스케이프 처리
                const escapedName = item.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(escapedName, 'g');
                let match;
                while ((match = regex.exec(text)) !== null) {
                    allMatches.push({
                        index: match.index,
                        length: match[0].length,
                        color: item.color,
                        backgroundColor: (item as { name: string; color: string; backgroundColor?: string })
                            .backgroundColor,
                        text: match[0],
                    });
                }
            });

            // 2단계: 겹치는 매칭 제거 (긴 매칭이 우선)
            // 예: "체지방량"과 "체지방"이 겹치면 "체지방량"만 선택
            const nonOverlappingMatches: typeof allMatches = [];
            const usedRanges: Array<{ start: number; end: number }> = [];

            // 길이 순으로 정렬 (긴 것 우선, 같은 길이면 앞쪽 우선)
            const sortedMatches = [...allMatches].sort((a, b) => {
                if (a.length !== b.length) return b.length - a.length; // 긴 것 우선
                return a.index - b.index; // 같은 길이면 앞쪽 우선
            });

            // 겹치는 범위를 제거하여 중복 치환 방지
            for (const match of sortedMatches) {
                const start = match.index;
                const end = start + match.length;
                // 이미 사용된 범위와 겹치는지 확인
                const overlaps = usedRanges.some(range => start < range.end && end > range.start);
                if (!overlaps) {
                    nonOverlappingMatches.push(match);
                    usedRanges.push({ start, end });
                }
            }

            // 3단계: 역순으로 정렬하여 뒤에서부터 치환
            // 앞에서부터 치환하면 offset이 변경되어 뒤쪽 매칭 위치가 틀어지므로 역순으로 처리
            nonOverlappingMatches.sort((a, b) => b.index - a.index);

            // 역순으로 치환 수행
            for (const match of nonOverlappingMatches) {
                const before = text.substring(0, match.index);
                const matched = text.substring(match.index, match.index + match.length);
                const after = text.substring(match.index + match.length);
                // skipBackgroundColor가 true이면 배경색을 포함하지 않고 data 속성에만 저장
                const backgroundColorStyle =
                    !skipBackgroundColor && match.backgroundColor
                        ? `background-color: ${match.backgroundColor}; padding: 0 0.8rem; border-radius: 5rem;`
                        : '';
                const dataAttributes = match.backgroundColor
                    ? ` data-has-bg="true" data-bg-color="${match.backgroundColor}" data-bg-padding="0 0.8rem" data-bg-radius="5rem"`
                    : '';
                text =
                    before +
                    `<span style="color: ${match.color};${backgroundColorStyle}"${dataAttributes}>${matched}</span>` +
                    after;
            }

            // 4단계: 텍스트가 변경된 경우 새로운 HTML로 교체
            // 치환이 발생한 경우 텍스트 노드를 HTML fragment로 교체
            if (text !== originalText) {
                const fragment = document.createDocumentFragment();
                const temp = document.createElement('div');
                temp.innerHTML = text;
                while (temp.firstChild) {
                    fragment.appendChild(temp.firstChild);
                }
                node.parentNode?.replaceChild(fragment, node);
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // @name@, #name# 전처리로 생성된 요소는 키워드 매칭에서 제외
            const element = node as HTMLElement;
            if (element.classList?.contains('kw-no-highlight') || element.classList?.contains('kw-highlighted')) {
                return;
            }
            // 요소 노드의 자식들을 재귀적으로 처리
            // HTML 태그 내부의 텍스트 노드도 처리하기 위해 재귀 호출
            const children = Array.from(node.childNodes);
            children.forEach(child => replaceInTextNode(child));
        }
    }

    // 모든 텍스트 노드에 대해 치환 수행
    replaceInTextNode(tempDiv);

    bodyText = tempDiv.innerHTML;

    if (moduleName === 'CommentModule') {
        bodyText = bodyText.replace(/(<br>\s*){2,}/g, '<br>');
        bodyText = bodyText.replace(/<br>/g, '<br><br>');
    }

    return bodyText;
}

function splitHtmlBySentence(html: string): string[] {
    // 1. 임시 DOM에 html 파싱
    const container = document.createElement('div');
    container.innerHTML = html;

    let buffer = '';
    let result: string[] = [];
    function traverse(node: Node) {
        if (node.nodeType === Node.TEXT_NODE) {
            // 텍스트는 마침표 기준으로 split (분리구현)
            let text = node.textContent || '';
            while (text.length > 0) {
                // 문장 경계 탐색
                const match = text.match(/.*?[.!?](\s|$)/);
                if (match) {
                    buffer += match[0];
                    result.push(buffer);
                    buffer = '';
                    text = text.slice(match[0].length);
                } else {
                    buffer += text;
                    text = '';
                }
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // 열기 태그
            const outer = node as HTMLElement;
            if (outer.tagName.toLowerCase() === 'br') {
                // br 태그가 중복으로 들어가는 것을 방지하기 위해 재귀 중단
                buffer += '<br>';
                // 재귀 중단
                return;
            }
            const startTag = `<${outer.tagName.toLowerCase()}${Array.from(outer.attributes)
                .map(attr => ` ${attr.name}="${attr.value}"`)
                .join('')}>`;
            buffer += startTag;
            // 재귀
            Array.from(node.childNodes).forEach(traverse);
            // 닫기 태그
            buffer += `</${outer.tagName.toLowerCase()}>`;
        }
    }
    Array.from(container.childNodes).forEach(traverse);

    // 마지막에 남은 buffer
    if (buffer.trim().length > 0) {
        result.push(buffer);
    }

    // 빈 항목 제거
    return result.map(s => s.trim()).filter(s => s.length > 0);
}

function wrapHtmlSentences(html: string): string {
    const sentences = splitHtmlBySentence(html);
    return sentences.map(s => `<p class="sentence-wrap">${s}</p>`).join(' ');
}

// 2. 글자별 span화 함수 (문장별로)
function spanifyChars(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        // 이미 래핑된 경우 중복 spanify 방지
        if (node.parentNode && (node.parentNode as HTMLElement).classList?.contains('char-spanified')) return;

        const parent = node.parentNode as HTMLElement;
        // 부모가 하이라이트된 span인 경우, 하이라이트 스타일을 유지하면서 글자별로 나눔
        if (
            parent &&
            parent.tagName.toLowerCase() === 'span' &&
            (parent.style.color || parent.getAttribute('data-highlighted') === 'true')
        ) {
            const color = parent.style.color || parent.getAttribute('data-highlight-color') || '';
            // data 속성을 우선 확인 (이미 배경색이 제거된 경우를 위해)
            const bgColor = parent.getAttribute('data-highlight-bg') || parent.style.backgroundColor || '';
            const padding = parent.getAttribute('data-bg-padding') || parent.style.padding || '';
            const borderRadius = parent.getAttribute('data-bg-radius') || parent.style.borderRadius || '';
            const keywordText = text; // 원본 키워드 텍스트 저장

            const keywordId = parent.getAttribute('data-keyword-id') || '';
            const frag = document.createDocumentFragment();
            const chars = [...text];
            chars.forEach((char, index) => {
                const span = document.createElement('span');
                span.textContent = char;
                span.style.opacity = '0';
                span.style.transition = `opacity ${(Math.random() * 0.11 + 0.1).toFixed(3)}s`;
                // 텍스트 컬러는 즉시 적용
                if (color) span.style.color = color;
                // 배경색 정보는 data 속성에 저장 (나중에 마지막 글자에서 적용)
                if (bgColor && keywordId) {
                    span.setAttribute('data-has-bg', 'true');
                    span.setAttribute('data-bg-color', bgColor);
                    span.setAttribute('data-bg-padding', padding || '0 0.8rem');
                    span.setAttribute('data-bg-radius', borderRadius || '5rem');
                    span.setAttribute('data-keyword-text', keywordText);
                    span.setAttribute('data-keyword-id', keywordId);
                    span.setAttribute('data-char-index', index.toString());
                }
                frag.appendChild(span);
            });
            // 하이라이트된 span을 글자별 span들로 교체
            const parentParent = parent.parentNode;
            if (parentParent) {
                parentParent.replaceChild(frag, parent);
            }
            return;
        }

        const frag = document.createDocumentFragment();
        [...text].forEach(char => {
            const span = document.createElement('span');
            span.textContent = char;
            span.style.opacity = '0';
            span.style.transition = `opacity ${(Math.random() * 0.11 + 0.1).toFixed(3)}s`;
            frag.appendChild(span);
        });
        node.parentNode?.replaceChild(frag, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        // 하이라이트된 span인 경우, 내부 텍스트를 글자별로 나누되 스타일은 유지
        if (
            element.tagName.toLowerCase() === 'span' &&
            (element.style.color || element.getAttribute('data-highlighted') === 'true')
        ) {
            // 하이라이트된 span 내부의 텍스트 노드들을 찾아서 글자별로 나눔
            const textNodes: Text[] = [];
            function collectTextNodes(n: Node) {
                if (n.nodeType === Node.TEXT_NODE) {
                    textNodes.push(n as Text);
                } else if (n.nodeType === Node.ELEMENT_NODE) {
                    Array.from(n.childNodes).forEach(collectTextNodes);
                }
            }
            Array.from(element.childNodes).forEach(collectTextNodes);

            if (textNodes.length > 0) {
                const color = element.style.color || element.getAttribute('data-highlight-color') || '';
                // data 속성을 우선 확인 (이미 배경색이 제거된 경우를 위해)
                const bgColor = element.getAttribute('data-highlight-bg') || element.style.backgroundColor || '';
                const padding = element.getAttribute('data-bg-padding') || element.style.padding || '';
                const borderRadius = element.getAttribute('data-bg-radius') || element.style.borderRadius || '';
                const keywordId = element.getAttribute('data-keyword-id') || '';
                // 원본 키워드 텍스트 추출
                const keywordText = textNodes.map(n => n.textContent || '').join('');

                // 하이라이트된 span을 글자별 span들로 교체
                const frag = document.createDocumentFragment();
                let globalIndex = 0;
                textNodes.forEach(textNode => {
                    const text = textNode.textContent || '';
                    const chars = [...text];
                    chars.forEach(char => {
                        const span = document.createElement('span');
                        span.textContent = char;
                        span.style.opacity = '0';
                        span.style.transition = `opacity ${(Math.random() * 0.11 + 0.1).toFixed(3)}s`;
                        // 텍스트 컬러는 즉시 적용
                        if (color) span.style.color = color;
                        // 배경색 정보는 data 속성에 저장 (나중에 마지막 글자에서 적용)
                        if (bgColor && keywordId) {
                            span.setAttribute('data-has-bg', 'true');
                            span.setAttribute('data-bg-color', bgColor);
                            span.setAttribute('data-bg-padding', padding || '0 0.8rem');
                            span.setAttribute('data-bg-radius', borderRadius || '5rem');
                            span.setAttribute('data-keyword-text', keywordText);
                            span.setAttribute('data-keyword-id', keywordId);
                            span.setAttribute('data-char-index', globalIndex.toString());
                        }
                        frag.appendChild(span);
                        globalIndex++;
                    });
                });

                const parent = element.parentNode;
                if (parent) {
                    parent.replaceChild(frag, element);
                }
                return;
            }
        }
        // 태그는 유지, 하위만 재귀 spanify
        Array.from(node.childNodes).forEach(spanifyChars);
    }
}

const animateCharacters = async (el: HTMLElement) => {
    // 하이라이트 및 줄바꿈 (애니메이션용이므로 배경색은 포함하지 않음)
    let html = getColoredHtml(props.html, props.moduleName, true);
    html = html.replace(/\\n/g, '<br>');
    // 문장별로 한 덩어리 래핑
    html = wrapHtmlSentences(html);

    el.innerHTML = '';
    const container = document.createElement('div');
    container.innerHTML = html;

    // 하이라이트된 span을 임시로 보호하기 위해 특별한 클래스 추가
    let keywordIdCounter = 0;
    container.querySelectorAll('span[style*="color"]').forEach(span => {
        const spanEl = span as HTMLElement;
        spanEl.setAttribute('data-highlighted', 'true');
        // 하이라이트된 span의 스타일을 data 속성에 저장
        const color = spanEl.style.color;
        if (color) {
            spanEl.setAttribute('data-highlight-color', color);
            // 배경색이 있는 경우 고유 ID 부여
            if (spanEl.getAttribute('data-has-bg') === 'true') {
                spanEl.setAttribute('data-keyword-id', `keyword-${keywordIdCounter++}`);
            }
        }
    });

    // sentence-wrap 내에 글자별 span화
    container.querySelectorAll('.sentence-wrap').forEach(sentenceEl => {
        spanifyChars(sentenceEl as HTMLElement);
    });

    // spanifyChars에서 하이라이트된 span을 글자별로 나누면서 처리하므로
    // 여기서는 별도 복원이 필요 없음 (배경색은 마지막 글자에서 애니메이션 중에 적용됨)

    Array.from(container.childNodes).forEach(child => el.appendChild(child));

    // 이후 애니메이션 부분
    const sentenceEls = el.querySelectorAll('.sentence-wrap');
    for (let i = 0; i < sentenceEls.length; i++) {
        const sentenceEl = sentenceEls[i] as HTMLElement;
        const charSpans = sentenceEl.querySelectorAll('span');
        for (let j = 0; j < charSpans.length; j++) {
            const charSpan = charSpans[j] as HTMLElement;
            charSpan.style.opacity = '1';

            // 배경색이 있는 경우, 마지막 글자에서 배경색 적용
            if (charSpan.getAttribute('data-has-bg') === 'true') {
                const keywordText = charSpan.getAttribute('data-keyword-text') || '';
                const keywordId = charSpan.getAttribute('data-keyword-id') || '';
                const charIndex = parseInt(charSpan.getAttribute('data-char-index') || '0', 10);
                // 마지막 글자인지 확인
                if (charIndex === keywordText.length - 1 && keywordId) {
                    const bgColor = charSpan.getAttribute('data-bg-color');
                    if (bgColor) {
                        // 같은 키워드 ID를 가진 모든 글자들에 배경색 적용
                        const allKeywordSpans = el.querySelectorAll(`[data-keyword-id="${keywordId}"]`);
                        allKeywordSpans.forEach((span: Element) => {
                            const spanEl = span as HTMLElement;
                            if (spanEl.getAttribute('data-bg-color')) {
                                spanEl.style.backgroundColor = spanEl.getAttribute('data-bg-color') || '';
                                const spanPadding = spanEl.getAttribute('data-bg-padding');
                                const spanRadius = spanEl.getAttribute('data-bg-radius');
                                if (spanPadding) spanEl.style.padding = spanPadding;
                                if (spanRadius) spanEl.style.borderRadius = spanRadius;
                            }
                        });
                    }
                }
            }

            await new Promise(r => setTimeout(r, Math.floor(Math.random() * 11) + 8));
        }
        if (i < sentenceEls.length - 1) {
            const allSentences = Array.from(sentenceEls).map(el => el.textContent || '');
            const pause = getSmartPauseByLength(allSentences[i]);
            await new Promise(r => setTimeout(r, pause));
        }
    }
    emit('done');
};

function getSmartPauseByLength(sentence: string) {
    // ★ 1. pause base는 길이에 따라 가중치 부여
    let base = sentence.length < 18 ? 25 : sentence.length > 45 ? 90 : 50;

    // ★ 2. 로그분포 느낌의 랜덤 pause (짧은 게 많고, 아주 긴 게 가끔)
    let pauseRandomizer = Math.pow(Math.random(), 3);

    // ★ 3. 간극 크게, 긴 pause도 자주
    let pause = base + pauseRandomizer * 600 + 40;

    // // ★ 4. 아주 가끔 초장기 pause (2% 확률)
    // if (Math.random() > 0.98) {
    //     pause += 1500 + Math.random() * 1200;
    // }

    return pause;
}

watch(
    [() => props.isActive, () => props.showAllImmediately, () => props.html, () => props.moduleName],
    async ([active, showAllImmediately]) => {
        if (!props.html) return;
        await nextTick();

        // 1. 하이라이트 및 줄바꿈 처리
        let html = getColoredHtml(props.html, props.moduleName);
        html = html.replace(/\\n/g, '<br>');

        // 2. 문장별로 태그까지 한 덩어리로 래핑
        html = wrapHtmlSentences(html);

        // 3. DOM으로 파싱
        const temp = document.createElement('div');
        temp.innerHTML = html;

        if (container.value) {
            container.value.innerHTML = '';
            Array.from(temp.childNodes).forEach(child => container.value?.appendChild(child));

            if (!showAllImmediately && active) {
                animateCharacters(container.value);
            } else {
                emit('done');
            }
        }
    },
    { immediate: true },
);
</script>

<style scoped lang="scss">
.animated-html-text {
    display: inline-block;
    white-space: pre-line;
}
:deep(.sentence-wrap) {
    display: inline;
}
</style>
